import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { map, merge, startWith } from 'rxjs';

import { JeptoUiInputComponent } from '@framework/ui-lib/form-controls/jepto-ui-input/jepto-ui-input.component';
import { JeptoButtonComponent } from '@framework/ui-lib/button/button.component';

import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';
import { SharedModule } from '@jepto/shared/shared.module';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';
import * as fromSharedStore from '@jepto/shared/store/index';

import type { AiCreditsIncentiveItem } from '../../models/ai-credits-incentive.model';
import { AiCreditsIncentiveStore } from '../../stores/ai-credits-incentive.store';
import { buildAiCreditsIncentiveSuccessMessage } from '../../utils/ai-credits-incentive-success-message.util';
import {
	buildAccountPathUrl,
	normaliseDomain,
	validateIncentiveProofUrl,
} from '../../utils/ai-credits-incentive-url.util';

export interface AiCreditsIncentiveModalData {
	/**
	 * The incentive as the panel last saw it. Carrying the whole item rather
	 * than just its definition lets the modal reopen on a rejected task with
	 * the reason it was rejected still on screen.
	 */
	readonly incentive: AiCreditsIncentiveItem;
}

/** Where the account's website is edited, for MISSING_ACCOUNT_DOMAIN. */
const ACCOUNT_SETTINGS_PATH = ['settings', 'account'];

@Component({
	selector: 'jepto-ai-credits-incentive-modal',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		JeptoUiInputComponent,
		JeptoButtonComponent,
		SharedModule,
	],
	templateUrl: './jepto-ai-credits-incentive-modal.component.html',
	styleUrl: './jepto-ai-credits-incentive-modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	host: { class: 'jepto-ai-credits-incentive-modal' },
})
export class JeptoAiCreditsIncentiveModalComponent {
	private readonly dialogRef = inject(
		MatDialogRef<JeptoAiCreditsIncentiveModalComponent, boolean>
	);
	private readonly incentiveStore = inject(AiCreditsIncentiveStore);
	private readonly snackBar = inject(MatSnackBar);
	private readonly store = inject(Store<fromSharedStore.SharedModuleReducers>);
	private readonly destroyRef = inject(DestroyRef);
	readonly data = inject<AiCreditsIncentiveModalData>(MAT_DIALOG_DATA);

	/** Set when MatDialog tears the modal down (including closeAll on task switch). */
	private dismissed = false;

	constructor() {
		this.destroyRef.onDestroy(() => {
			this.dismissed = true;
		});
	}

	private readonly accountData = this.store.selectSignal(
		fromSharedAccountStore.getAccountData
	);

	/**
	 * The account's own domain, used to keep backlink submissions to a page the
	 * account actually owns.
	 */
	private readonly accountDomain = computed(
		() => this.accountData()?.domain ?? null
	);

	/** Backlink proof URLs are matched against the account's own domain. */
	private readonly requiresAccountDomain =
		this.data.incentive.urlInputMode === 'account-path';

	/**
	 * Read-only prefix shown before the input, e.g. `https://acme.com/`, so the
	 * own-site requirement is visible up front instead of arriving as an error.
	 */
	readonly urlPrefix = computed(() => {
		if (!this.requiresAccountDomain) {
			return '';
		}
		const domain = normaliseDomain(this.accountDomain());
		return domain ? `https://${domain}/` : '';
	});

	/**
	 * Account state starts null and fills in once loaded, so "not loaded yet" has
	 * to be told apart from "loaded, but no domain on file".
	 */
	readonly isAccountLoading = computed(
		() => this.requiresAccountDomain && this.accountData() == null
	);

	/**
	 * The domain is required at signup but can be cleared in account settings.
	 * Without one there is nothing to match a backlink against, so block the
	 * submission rather than letting any URL through.
	 */
	readonly missingAccountDomain = computed(
		() =>
			this.requiresAccountDomain &&
			this.accountData() != null &&
			!normaliseDomain(this.accountDomain())
	);

	readonly formValidated = signal(false);

	readonly form = new FormGroup({
		proofUrl: new FormControl('', {
			nonNullable: true,
			validators: [Validators.required],
		}),
	});

	private readonly formState = toSignal(
		merge(this.form.valueChanges, this.form.statusChanges).pipe(
			startWith(null),
			map(() => ({
				proofUrl: this.form.controls.proofUrl.value.trim(),
				valid: this.form.valid,
			}))
		),
		{
			initialValue: {
				proofUrl: '',
				valid: this.form.valid,
			},
		}
	);

	/**
	 * The value to submit. In `account-path` mode the field holds only the path,
	 * so the account domain is prepended to make a whole URL.
	 */
	private resolveProofUrl(raw: string): string | null {
		if (!this.requiresAccountDomain) {
			return raw;
		}
		return buildAccountPathUrl(this.accountDomain(), raw);
	}

	readonly proofUrlError = computed(() => {
		if (!this.formValidated()) {
			return '';
		}
		// Read via formState so typing after a failed submit recomputes
		// the message; the raw FormControl value is not a signal dependency.
		const raw = this.formState().proofUrl;
		if (!raw) {
			return this.requiresAccountDomain
				? 'Page path is required'
				: 'URL is required';
		}

		const resolved = this.resolveProofUrl(raw);
		if (!resolved) {
			return 'Enter a valid page path';
		}

		return (
			validateIncentiveProofUrl(
				this.data.incentive.id,
				resolved,
				this.accountDomain()
			) ?? ''
		);
	});

	readonly canSubmit = computed(() => {
		const { proofUrl: raw } = this.formState();
		if (this.isAccountLoading() || this.missingAccountDomain()) {
			return false;
		}
		if (!raw || this.incentiveStore.submitState() === 'loading') {
			return false;
		}

		return true;
	});

	readonly submitError = computed(() => this.incentiveStore.submitError());
	readonly isSubmitting = computed(
		() => this.incentiveStore.submitState() === 'loading'
	);

	/**
	 * The domain is what a backlink is matched against, so when it is missing
	 * there is nothing to fix inside this modal. Offered both for the case we
	 * catch client-side and for MISSING_ACCOUNT_DOMAIN coming back from the
	 * server, which can still happen if the domain is cleared mid-session.
	 */
	readonly needsAccountSettingsLink = computed(
		() =>
			this.missingAccountDomain() ||
			this.incentiveStore.submitRejectionCode() ===
				'MISSING_ACCOUNT_DOMAIN'
	);

	close(): void {
		this.incentiveStore.clearSubmitError();
		this.dialogRef.close(false);
	}

	/**
	 * Closes first: the dialog is `disableClose`, so it would otherwise sit over
	 * the settings page the user was just sent to.
	 */
	goToAccountSettings(): void {
		this.close();
		this.store.dispatch(
			new fromSharedStore.Go({ path: ACCOUNT_SETTINGS_PATH })
		);
	}

	openExternalCta(): void {
		const cta = this.data.incentive.externalCta;
		if (!cta?.url) {
			return;
		}
		window.open(cta.url, '_blank', 'noopener,noreferrer');
	}

	async onSubmit(): Promise<void> {
		this.formValidated.set(true);
		this.incentiveStore.clearSubmitError();
		this.form.controls.proofUrl.markAsTouched();

		if (this.isAccountLoading() || this.missingAccountDomain()) {
			return;
		}

		const raw = this.form.controls.proofUrl.value.trim();
		const resolved = this.resolveProofUrl(raw);
		if (!raw || !resolved || this.form.invalid) {
			return;
		}

		const validationError = validateIncentiveProofUrl(
			this.data.incentive.id,
			resolved,
			this.accountDomain()
		);
		if (validationError) {
			return;
		}

		const success = await this.incentiveStore.submitIncentive(
			this.data.incentive.id,
			resolved
		);
		// closeAll() / destroy can race a still-pending submit — do not toast or
		// close for a task the user already left.
		if (this.dismissed || !success) {
			return;
		}
		this.#showSubmitSuccessSnackbar();
		this.dialogRef.close(true);
	}

	#showSubmitSuccessSnackbar(): void {
		this.snackBar.openFromComponent(JepSnackbarComponent, {
			duration: 5000,
			data: {
				text: buildAiCreditsIncentiveSuccessMessage(this.data.incentive),
				status: 'success',
				icon: 'info',
			},
		});
	}
}
